import React, { useState, useCallback, useEffect, useRef } from 'react';
import { BarChart3, Minimize2 } from 'lucide-react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { HealthCalendarTile, DailyHealthData, GoalTarget } from '../client/HealthCalendarTile';
import { WeightGaugeV2 } from '../client/WeightGaugeV2';
import { WeightProgressData } from '../../hooks/useWeightProgress';
import { CICCalculatorTile } from './CICCalculatorTile';
import { NutritionDayView } from './NutritionDayView';
import { getMetricComplianceColor } from '../../utils/goalCompliance';

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * ANALYTICS EXPANDED VIEW - Single pane of glass for coaches
 *
 * Layout:
 * - Top half: Compact calendar (left) | Middle tiles (center) | Macro donut + wellness (right)
 * - Bottom half: Full-width analytics table with day-by-day metrics
 *
 * Selection model: click day, ctrl+click week, ESC to deselect
 */

interface CalendarExpandedViewProps {
  clientId: string;
  clientName: string;
  clientTimezone?: string;
  onClose: (month: number, year: number) => void;
  initialMonth?: number;
  initialYear?: number;
}

interface MacroData {
  protein_g: number;
  carbs_g: number;
  fats_g: number;
  calories: number;
}

interface WellnessData {
  steps: number | null;
  sleep_hours: number | null;
  water_intake: number | null;
  energy_level: number | null;
  stress_level: number | null;
  weight: number | null;
}

function computeMonthlyAverages(data: DailyHealthData[]): { macros: MacroData; wellness: WellnessData; daysWithData: number } {
  const daysWithNutrition: MacroData[] = [];
  const wellnessAccum = { steps: [] as number[], sleep: [] as number[], water: [] as number[], energy: [] as number[], stress: [] as number[], weight: [] as number[] };

  for (const day of data) {
    const av = day.actual_values;
    if (!av) continue;

    // Nutrition — only count days that have calorie data
    if (av.calories && av.calories > 0) {
      daysWithNutrition.push({
        protein_g: av.protein_g || 0,
        carbs_g: av.carbs_g || 0,
        fats_g: av.fats_g || 0,
        calories: av.calories
      });
    }

    // Wellness — accumulate non-null values
    if (av.steps != null && av.steps > 0) wellnessAccum.steps.push(av.steps);
    if (av.sleep_hours != null && av.sleep_hours > 0) wellnessAccum.sleep.push(av.sleep_hours);
    if (av.water_intake != null && av.water_intake > 0) wellnessAccum.water.push(av.water_intake);
    if (av.energy_level != null) wellnessAccum.energy.push(av.energy_level);
    if (av.stress_level != null) wellnessAccum.stress.push(av.stress_level);
    if (av.weight != null && av.weight > 0) wellnessAccum.weight.push(av.weight);
  }

  const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
  const avgOrNull = (arr: number[]) => arr.length > 0 ? avg(arr) : null;

  const macros: MacroData = daysWithNutrition.length > 0
    ? {
        protein_g: avg(daysWithNutrition.map(d => d.protein_g)),
        carbs_g: avg(daysWithNutrition.map(d => d.carbs_g)),
        fats_g: avg(daysWithNutrition.map(d => d.fats_g)),
        calories: avg(daysWithNutrition.map(d => d.calories))
      }
    : { protein_g: 0, carbs_g: 0, fats_g: 0, calories: 0 };

  return {
    macros,
    wellness: {
      steps: avgOrNull(wellnessAccum.steps),
      sleep_hours: avgOrNull(wellnessAccum.sleep),
      water_intake: avgOrNull(wellnessAccum.water),
      energy_level: avgOrNull(wellnessAccum.energy),
      stress_level: avgOrNull(wellnessAccum.stress),
      weight: avgOrNull(wellnessAccum.weight)
    },
    daysWithData: daysWithNutrition.length
  };
}

function extractDayData(day: DailyHealthData | null): { macros: MacroData; wellness: WellnessData } | null {
  if (!day?.actual_values) return null;
  const av = day.actual_values;
  return {
    macros: {
      protein_g: av.protein_g || 0,
      carbs_g: av.carbs_g || 0,
      fats_g: av.fats_g || 0,
      calories: av.calories || 0
    },
    wellness: {
      steps: av.steps ?? null,
      sleep_hours: av.sleep_hours ?? null,
      water_intake: av.water_intake ?? null,
      energy_level: av.energy_level ?? null,
      stress_level: av.stress_level ?? null,
      weight: av.weight ?? null
    }
  };
}

// Get Monday of the week containing the given date (coaching week = Mon-Sun)
function getWeekMonday(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = d.getDay(); // 0=Sun, 1=Mon, ...
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(d);
  monday.setDate(monday.getDate() + mondayOffset);
  return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
}

function getWeekSunday(mondayStr: string): string {
  const d = new Date(mondayStr + 'T12:00:00');
  d.setDate(d.getDate() + 6);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type SelectionMode = 'month' | 'day' | 'week';

export const CalendarExpandedView: React.FC<CalendarExpandedViewProps> = ({
  clientId,
  clientName,
  clientTimezone,
  onClose,
  initialMonth,
  initialYear
}) => {
  const [healthData, setHealthData] = useState<DailyHealthData[]>([]);
  const [overflowData, setOverflowData] = useState<DailyHealthData[]>([]);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('month');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedDayData, setSelectedDayData] = useState<DailyHealthData | null>(null);
  const [selectedWeekStart, setSelectedWeekStart] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(initialMonth ?? new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(initialYear ?? new Date().getFullYear());

  // Nutrition day detail state (for clickable dates in analytics table)
  const [nutritionDetailDate, setNutritionDetailDate] = useState<string | null>(null);

  // Weight Progress tile state
  const [weightProgressData, setWeightProgressData] = useState<WeightProgressData | null>(null);
  const [weightProgressLoading, setWeightProgressLoading] = useState(true);
  const [weightProgressError, setWeightProgressError] = useState<string | null>(null);

  // Fetch weight progress data for this client
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token || !clientId) return;

    setWeightProgressLoading(true);
    setWeightProgressError(null);

    const abortController = new AbortController();
    fetch(`/api/coach/clients/${clientId}/weight-progress`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal: abortController.signal
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setWeightProgressData(data);
        } else {
          setWeightProgressError(data.error || 'Failed to load weight progress');
        }
        setWeightProgressLoading(false);
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          setWeightProgressError('Failed to load weight progress');
          setWeightProgressLoading(false);
        }
      });

    return () => abortController.abort();
  }, [clientId]);

  // Fetch adjacent month data when a week crosses a month boundary
  useEffect(() => {
    if (!selectedWeekStart) {
      setOverflowData([]);
      return;
    }
    const weekEnd = getWeekSunday(selectedWeekStart);
    const weekStartMonth = parseInt(selectedWeekStart.split('-')[1]) - 1;
    const weekEndMonth = parseInt(weekEnd.split('-')[1]) - 1;
    const weekStartYear = parseInt(selectedWeekStart.split('-')[0]);
    const weekEndYear = parseInt(weekEnd.split('-')[0]);

    // No overflow if week is entirely within the current month
    if (weekStartMonth === currentMonth && weekStartYear === currentYear &&
        weekEndMonth === currentMonth && weekEndYear === currentYear) {
      setOverflowData([]);
      return;
    }

    // Determine which adjacent month to fetch
    const adjMonth = weekStartMonth !== currentMonth || weekStartYear !== currentYear
      ? { month: weekStartMonth, year: weekStartYear }
      : { month: weekEndMonth, year: weekEndYear };

    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const abortController = new AbortController();
    fetch(
      `/api/coach/clients/${clientId}/metrics/calendar?month=${adjMonth.month + 1}&year=${adjMonth.year}`,
      { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, signal: abortController.signal }
    )
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setOverflowData(data.data?.healthData || []);
        }
      })
      .catch(() => { /* ignore abort/fetch errors */ });

    return () => abortController.abort();
  }, [selectedWeekStart, currentMonth, currentYear, clientId]);

  // Compute what to display based on selection mode
  const monthlyAvg = computeMonthlyAverages(healthData);

  // Week averages: merge current month + overflow data, filter to selected week
  const weekAvg = selectedWeekStart ? (() => {
    const weekEnd = getWeekSunday(selectedWeekStart);
    const allData = [...healthData, ...overflowData];
    // Deduplicate by date (in case both months include the same day)
    const seen = new Set<string>();
    const weekData = allData.filter(d => {
      if (d.date < selectedWeekStart! || d.date > weekEnd || seen.has(d.date)) return false;
      seen.add(d.date);
      return true;
    });
    return computeMonthlyAverages(weekData);
  })() : null;

  const daySpecific = selectedDayData ? extractDayData(selectedDayData) : null;

  const displayMacros = selectionMode === 'day' && daySpecific ? daySpecific.macros
    : selectionMode === 'week' && weekAvg ? weekAvg.macros
    : monthlyAvg.macros;
  const clearSelection = useCallback(() => {
    setSelectionMode('month');
    setSelectedDate(null);
    setSelectedDayData(null);
    setSelectedWeekStart(null);
  }, []);

  // ESC key: if day/week is selected → deselect (don't close expanded view)
  useEffect(() => {
    if (selectionMode === 'month') return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        e.preventDefault();
        clearSelection();
      }
    };

    window.addEventListener('keydown', handleEsc, true); // capture phase
    return () => window.removeEventListener('keydown', handleEsc, true);
  }, [selectionMode, clearSelection]);

  // ESC to close nutrition day detail
  useEffect(() => {
    if (!nutritionDetailDate) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        e.preventDefault();
        setNutritionDetailDate(null);
      }
    };
    window.addEventListener('keydown', handleEsc, true);
    return () => window.removeEventListener('keydown', handleEsc, true);
  }, [nutritionDetailDate]);

  const handleDataLoaded = useCallback((data: DailyHealthData[]) => {
    setHealthData(data);
    clearSelection();
  }, [clearSelection]);

  const handleDayClick = useCallback((date: string, data: DailyHealthData | null, ctrlKey: boolean) => {
    // Empty/invalid tile or no data → deselect
    if (!date || !data) {
      clearSelection();
      return;
    }

    if (ctrlKey) {
      // Ctrl+Click → week selection
      const weekMonday = getWeekMonday(date);
      if (selectedWeekStart === weekMonday) {
        clearSelection(); // toggle off same week
      } else {
        setSelectionMode('week');
        setSelectedWeekStart(weekMonday);
        setSelectedDate(null);
        setSelectedDayData(null);
      }
    } else {
      // Regular click → day selection (toggle off if same day)
      if (selectedDate === date) {
        clearSelection();
      } else {
        setSelectionMode('day');
        setSelectedDate(date);
        setSelectedDayData(data);
        setSelectedWeekStart(null);
      }
    }
  }, [selectedDate, selectedWeekStart, clearSelection]);

  // Handle click on a table row — mirrors calendar click behavior
  const handleTableRowClick = useCallback((date: string, ctrlKey: boolean) => {
    const dayData = healthData.find(d => d.date === date) || null;
    handleDayClick(date, dayData, ctrlKey);
  }, [healthData, handleDayClick]);

  // Compute selection-aware weight progress data
  // When a day or week is selected, compute weekly weight averages from calendar data
  // and overlay onto the API-fetched data (which provides journey/goal/streaks)
  const displayWeightProgress: WeightProgressData | null = (() => {
    if (!weightProgressData) return null;

    // Determine which week to show based on selection
    let targetWeekMonday: string | null = null;
    if (selectionMode === 'week' && selectedWeekStart) {
      targetWeekMonday = selectedWeekStart;
    } else if (selectionMode === 'day' && selectedDate) {
      targetWeekMonday = getWeekMonday(selectedDate);
    }

    // No selection or no target week — use API data as-is
    if (!targetWeekMonday) return weightProgressData;

    const targetWeekSunday = getWeekSunday(targetWeekMonday);

    // Compute previous week boundaries
    const prevMondayDate = new Date(targetWeekMonday + 'T12:00:00');
    prevMondayDate.setDate(prevMondayDate.getDate() - 7);
    const prevMonday = `${prevMondayDate.getFullYear()}-${String(prevMondayDate.getMonth() + 1).padStart(2, '0')}-${String(prevMondayDate.getDate()).padStart(2, '0')}`;
    const prevSunday = getWeekSunday(prevMonday);

    // Gather all available data (current month + overflow)
    const allData = [...healthData, ...overflowData];

    // Extract weight values for target week
    const targetWeights = allData
      .filter(d => d.date >= targetWeekMonday! && d.date <= targetWeekSunday && d.actual_values?.weight > 0)
      .map(d => d.actual_values!.weight);

    // Extract weight values for previous week
    const prevWeights = allData
      .filter(d => d.date >= prevMonday && d.date <= prevSunday && d.actual_values?.weight > 0)
      .map(d => d.actual_values!.weight);

    const targetAvg = targetWeights.length > 0
      ? Math.round((targetWeights.reduce((a, b) => a + b, 0) / targetWeights.length) * 10) / 10
      : null;
    let prevAvg = prevWeights.length > 0
      ? Math.round((prevWeights.reduce((a, b) => a + b, 0) / prevWeights.length) * 10) / 10
      : null;

    // No previous week data — fall back to starting weight (new client, first week)
    if (prevAvg === null && weightProgressData.startingWeight) {
      prevAvg = weightProgressData.startingWeight;
    }

    const weeklyChange = targetAvg !== null && prevAvg !== null
      ? Math.round((targetAvg - prevAvg) * 10) / 10
      : null;

    return {
      ...weightProgressData,
      currentWeek: {
        weekKey: targetWeekMonday,
        weekStart: targetWeekMonday,
        weekEnd: targetWeekSunday,
        average: targetAvg,
        entriesCount: targetWeights.length
      },
      previousWeek: { average: prevAvg },
      weeklyChange
    };
  })();

  // Daily weight comparison: selected day vs same day last week
  const [dailyWeightComparison, setDailyWeightComparison] = useState<{ dayWeight: number | null; dayWeightLastWeek: number | null } | undefined>(undefined);

  useEffect(() => {
    if (selectionMode !== 'day' || !selectedDate) {
      setDailyWeightComparison(undefined);
      return;
    }

    const allData = [...healthData, ...overflowData];
    const todayEntry = allData.find(d => d.date === selectedDate);
    const todayWeight = todayEntry?.actual_values?.weight > 0 ? todayEntry.actual_values.weight : null;

    // Same weekday, 7 days ago
    const lastWeekDate = new Date(selectedDate + 'T12:00:00');
    lastWeekDate.setDate(lastWeekDate.getDate() - 7);
    const lastWeekStr = `${lastWeekDate.getFullYear()}-${String(lastWeekDate.getMonth() + 1).padStart(2, '0')}-${String(lastWeekDate.getDate()).padStart(2, '0')}`;

    // Try local data first
    const lastWeekEntry = allData.find(d => d.date === lastWeekStr);
    if (lastWeekEntry) {
      const lastWeekWeight = lastWeekEntry.actual_values?.weight > 0 ? lastWeekEntry.actual_values.weight : null;
      setDailyWeightComparison({ dayWeight: todayWeight, dayWeightLastWeek: lastWeekWeight });
      return;
    }

    // Last week date not in loaded data (cross-month boundary) — fetch from API
    setDailyWeightComparison({ dayWeight: todayWeight, dayWeightLastWeek: null }); // show immediately with '--'
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const abortController = new AbortController();
    fetch(`/api/coach/clients/${clientId}/metrics/calendar?month=${lastWeekDate.getMonth() + 1}&year=${lastWeekDate.getFullYear()}`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal: abortController.signal
    })
      .then(r => r.json())
      .then(calData => {
        if (abortController.signal.aborted) return;
        const days: DailyHealthData[] = calData?.data?.healthData || [];
        const match = days.find((d: DailyHealthData) => d.date === lastWeekStr);
        const lastWeekWeight = match?.actual_values?.weight > 0 ? match.actual_values.weight : null;
        setDailyWeightComparison({ dayWeight: todayWeight, dayWeightLastWeek: lastWeekWeight });
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch last-week weight:', err);
        }
      });

    return () => abortController.abort();
  }, [selectionMode, selectedDate, clientId, healthData, overflowData]);

  // CIC Calculator data — selection-aware calorie averages and weight change
  const cicData = (() => {
    // Determine the data set and period based on selection
    let dataSet: DailyHealthData[] = [];
    let label = '';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    if (selectionMode === 'day' && selectedDate) {
      // Day mode: use the week containing the selected day
      const weekMon = getWeekMonday(selectedDate);
      const weekSun = getWeekSunday(weekMon);
      const allData = [...healthData, ...overflowData];
      const seen = new Set<string>();
      dataSet = allData.filter(d => {
        if (d.date < weekMon || d.date > weekSun || seen.has(d.date)) return false;
        seen.add(d.date);
        return true;
      });
      const monParts = weekMon.split('-');
      const sunParts = weekSun.split('-');
      label = `${monthNames[parseInt(monParts[1]) - 1]} ${parseInt(monParts[2])}–${parseInt(sunParts[2])}`;
    } else if (selectionMode === 'week' && selectedWeekStart) {
      const weekSun = getWeekSunday(selectedWeekStart);
      const allData = [...healthData, ...overflowData];
      const seen = new Set<string>();
      dataSet = allData.filter(d => {
        if (d.date < selectedWeekStart! || d.date > weekSun || seen.has(d.date)) return false;
        seen.add(d.date);
        return true;
      });
      const monParts = selectedWeekStart.split('-');
      const sunParts = weekSun.split('-');
      label = `${monthNames[parseInt(monParts[1]) - 1]} ${parseInt(monParts[2])}–${parseInt(sunParts[2])}`;
    } else {
      // Month mode: use all loaded health data
      dataSet = healthData;
      label = `${monthNames[currentMonth]} ${currentYear}`;
    }

    // Average calories for days that have calorie data
    const daysWithCals = dataSet.filter(d => d.actual_values?.calories && d.actual_values.calories > 0);
    const avgCalories = daysWithCals.length > 0
      ? daysWithCals.reduce((sum, d) => sum + (d.actual_values?.calories || 0), 0) / daysWithCals.length
      : null;

    // Weight: first and last available weight in the period
    const daysWithWeight = dataSet
      .filter(d => d.actual_values?.weight && d.actual_values.weight > 0)
      .sort((a, b) => a.date.localeCompare(b.date));

    const periodStartWeight = daysWithWeight.length > 0 ? daysWithWeight[0].actual_values!.weight! : null;
    const periodEndWeight = daysWithWeight.length > 1 ? daysWithWeight[daysWithWeight.length - 1].actual_values!.weight! : null;
    const periodDays = daysWithCals.length || 1;

    return { avgCalories, periodStartWeight, periodEndWeight, periodDays, periodLabel: label };
  })();

  // Macro donut data
  const proteinCal = displayMacros.protein_g * 4;
  const carbsCal = displayMacros.carbs_g * 4;
  const fatCal = displayMacros.fats_g * 9;
  const totalMacroCal = proteinCal + carbsCal + fatCal;

  const proteinPct = totalMacroCal > 0 ? Math.round((proteinCal / totalMacroCal) * 100) : 0;
  const carbsPct = totalMacroCal > 0 ? Math.round((carbsCal / totalMacroCal) * 100) : 0;
  const fatPct = totalMacroCal > 0 ? 100 - proteinPct - carbsPct : 0; // remainder to avoid rounding errors

  const donutData = {
    labels: ['Protein', 'Carbs', 'Fat'],
    datasets: [{
      data: totalMacroCal > 0 ? [proteinCal, carbsCal, fatCal] : [1, 1, 1],
      backgroundColor: totalMacroCal > 0
        ? ['#3b82f6', '#10b981', '#f59e0b']
        : ['#374151', '#374151', '#374151'],
      borderColor: 'transparent',
      borderWidth: 0
    }]
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const label = ctx.label;
            const val = ctx.raw;
            const pct = totalMacroCal > 0 ? Math.round((val / totalMacroCal) * 100) : 0;
            return `${label}: ${Math.round(val)} cal (${pct}%)`;
          }
        }
      }
    }
  };

  // Sort health data by date for the analytics table
  const sortedHealthData = [...healthData].sort((a, b) => a.date.localeCompare(b.date));

  // Compute prev-day weight change for each day
  const tableRows = sortedHealthData.map((day, idx) => {
    const av = day.actual_values;
    const weight = av?.weight ?? null;
    let prevDayChange: number | null = null;

    if (weight != null && idx > 0) {
      // Look backward for the most recent day with weight data
      for (let j = idx - 1; j >= 0; j--) {
        const prevWeight = sortedHealthData[j].actual_values?.weight;
        if (prevWeight != null && prevWeight > 0) {
          prevDayChange = weight - prevWeight;
          break;
        }
      }
    }

    return {
      date: day.date,
      weight,
      prevDayChange,
      calories: av?.calories ?? null,
      carbs: av?.carbs_g ?? null,
      protein: av?.protein_g ?? null,
      fat: av?.fats_g ?? null,
      sodium: av?.sodium_mg ?? null,
      steps: av?.steps ?? null,
      sleep: av?.sleep_hours ?? null,
      water: av?.water_intake ?? null,
      energy: av?.energy_level ?? null,
      stress: av?.stress_level ?? null,
      hasData: (av?.calories ?? 0) > 0 || (av?.weight ?? 0) > 0,
      targets: (day as any).targets_at_time as Record<string, GoalTarget> | undefined
    };
  });

  // Map analytics keys → targets_at_time field names (shared by row coloring + footer)
  const fieldMap: Record<string, string> = {
    calories: 'calories', carbs: 'carbs_g', protein: 'protein_g',
    fat: 'fats_g', sodium: 'sodium_mg', steps: 'steps',
    sleep: 'sleep_hours', water: 'water_intake',
    energy: 'energy_level', stress: 'stress_level'
  };

  // Metric units for tooltip display
  const metricUnits: Record<string, string> = {
    calories: 'kcal', carbs: 'g', protein: 'g', fat: 'g', sodium: 'mg',
    steps: '', sleep: 'hr', water: 'oz', energy: '', stress: ''
  };

  // Build goal tooltip text (mirrors NutritionTable pattern)
  const goalTooltip = (goal: GoalTarget | undefined, unit: string): string | undefined => {
    if (!goal) return undefined;
    const fmt = (v: number) => v.toLocaleString();
    switch (goal.goal_type) {
      case 'range':
        return `Goal: ${fmt(goal.floor_value || 0)} – ${fmt(goal.goal_value)}${unit ? ' ' + unit : ''}`;
      case 'over':
        return `Goal: Over ${fmt(goal.goal_value)}${unit ? ' ' + unit : ''}`;
      case 'under':
        return `Goal: Under ${fmt(goal.goal_value)}${unit ? ' ' + unit : ''}`;
      case 'target':
        return `Goal: ${fmt(goal.goal_value)}${unit ? ' ' + unit : ''} (±${(goal as any).variance_percent || 5}%)`;
      case 'tracking_only':
        return `Tracking only (no target)`;
      default:
        return undefined;
    }
  };

  // Get compliance color + tooltip for a row cell
  const cellStyle = (row: typeof tableRows[number], metricKey: string, value: number | null) => {
    if (!row.hasData || value == null) return {};
    const targetField = fieldMap[metricKey];
    if (!targetField) return {};
    const goal = row.targets?.[targetField];
    const color = getMetricComplianceColor(value, goal);
    const tip = goalTooltip(goal, metricUnits[metricKey] || '');
    return {
      style: color ? { color } : undefined,
      title: tip
    };
  };

  // Compute footer averages based on selection
  const getFooterData = () => {
    let rows = tableRows;
    let label = 'Month Avg';

    if (selectionMode === 'day' && selectedDate) {
      rows = tableRows.filter(r => r.date === selectedDate);
      label = 'Day Values';
    } else if (selectionMode === 'week' && selectedWeekStart) {
      const weekEnd = getWeekSunday(selectedWeekStart);
      rows = tableRows.filter(r => r.date >= selectedWeekStart! && r.date <= weekEnd);
      label = 'Week Avg';
    }

    const withData = rows.filter(r => r.hasData);
    const noData = { label, calories: null, carbs: null, protein: null, fat: null, sodium: null, steps: null, sleep: null, water: null, energy: null, stress: null, weight: null, weightDelta: null, colors: {} as Record<string, string | null> };
    if (withData.length === 0) return noData;

    const avg = (vals: (number | null)[]) => {
      const nums = vals.filter((v): v is number => v != null && v > 0);
      return nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
    };

    // Weight data for footer: only when day or week is selected
    let footerWeight: number | null = null;
    let footerWeightDelta: number | null = null;

    if (selectionMode === 'day' && withData.length === 1) {
      // Single day: show that day's actual weight and day-over-day delta
      footerWeight = withData[0].weight;
      footerWeightDelta = withData[0].prevDayChange;
    } else if (selectionMode === 'week') {
      // Week: show average weight and weekly change from progress gauge
      const weights = withData.map(r => r.weight).filter((w): w is number => w != null && w > 0);
      footerWeight = weights.length > 0
        ? Math.round((weights.reduce((a, b) => a + b, 0) / weights.length) * 10) / 10
        : null;

      if (footerWeight !== null && displayWeightProgress) {
        footerWeightDelta = displayWeightProgress.weeklyChange ?? null;
      }
    }

    const footerValues = {
      calories: avg(withData.map(r => r.calories)),
      carbs: avg(withData.map(r => r.carbs)),
      protein: avg(withData.map(r => r.protein)),
      fat: avg(withData.map(r => r.fat)),
      sodium: avg(withData.map(r => r.sodium)),
      steps: avg(withData.map(r => r.steps)),
      sleep: avg(withData.map(r => r.sleep)),
      water: avg(withData.map(r => r.water)),
      energy: avg(withData.map(r => r.energy)),
      stress: avg(withData.map(r => r.stress)),
    };

    // Footer compliance coloring: only for single-day selection (matches Nutrition table pattern)
    // Month/week averages show white text since goals may change across the period
    const colors: Record<string, string | null> = {};
    if (selectionMode === 'day' && withData.length === 1) {
      const goalSource = withData[0]?.targets;
      for (const [key, targetField] of Object.entries(fieldMap)) {
        const goal = goalSource?.[targetField];
        const value = footerValues[key as keyof typeof footerValues];
        colors[key] = getMetricComplianceColor(value, goal);
      }
    }

    return {
      label,
      ...footerValues,
      weight: footerWeight,
      weightDelta: footerWeightDelta,
      colors
    };
  };

  const footerData = getFooterData();

  // Ref for auto-scrolling table tbody (the actual scroll container in flex table layout)
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  // Auto-scroll table to center on highlighted row(s) when selection changes
  useEffect(() => {
    if (selectionMode === 'month') return;

    // Small delay to let the DOM update with highlight classes
    const timer = setTimeout(() => {
      const tbody = tbodyRef.current;
      if (!tbody) return;

      const highlightedRows = tbody.querySelectorAll('.highlighted-row');
      if (highlightedRows.length === 0) return;

      // Find the middle of the highlighted range relative to tbody's scroll area
      const firstRow = highlightedRows[0] as HTMLElement;
      const lastRow = highlightedRows[highlightedRows.length - 1] as HTMLElement;
      // offsetTop is relative to <table>, not <tbody> — subtract tbody's own offset
      const tbodyOffset = tbody.offsetTop;
      const rangeCenter = (
        (firstRow.offsetTop - tbodyOffset) +
        (lastRow.offsetTop - tbodyOffset) + lastRow.offsetHeight
      ) / 2;
      const tbodyCenter = tbody.clientHeight / 2;

      tbody.scrollTo({
        top: rangeCenter - tbodyCenter,
        behavior: 'smooth'
      });
    }, 50);

    return () => clearTimeout(timer);
  }, [selectionMode, selectedDate, selectedWeekStart]);

  // Check if a row is in the selected range
  const isRowHighlighted = (date: string) => {
    if (selectionMode === 'day') return date === selectedDate;
    if (selectionMode === 'week' && selectedWeekStart) {
      const weekEnd = getWeekSunday(selectedWeekStart);
      return date >= selectedWeekStart && date <= weekEnd;
    }
    return false;
  };

  const formatTableDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
  };

  return (
    <div className="calendar-expanded-view">
      {/* Header Bar */}
      <div className="cal-expanded-header">
        <div className="cal-header-left">
          <BarChart3 size={20} />
          <h2>Analytics</h2>
          <span className="cal-client-badge">{clientName}</span>
        </div>
        <div className="cal-header-right">
          <button
            className="cal-minimize-btn"
            onClick={() => onClose(currentMonth, currentYear)}
            title="Minimize (ESC)"
          >
            <Minimize2 size={16} />
          </button>
        </div>
      </div>

      {nutritionDetailDate ? (
        /* Day Detail View — fills entire content area below header */
        <div className="analytics-day-detail">
          <div className="analytics-day-detail-content">
            <NutritionDayView
              clientId={clientId}
              date={nutritionDetailDate}
              onBack={() => setNutritionDetailDate(null)}
            />
          </div>
        </div>
      ) : (
        <>
        {/* Top Half: Calendar + Middle Tiles + Analytics Panels */}
        <div className="analytics-top">
          {/* Left: Compact Calendar */}
          <div className="analytics-calendar">
            <HealthCalendarTile
              userId={clientId}
              clientTimezone={clientTimezone}
              onDayClick={handleDayClick}
              onDataLoaded={handleDataLoaded}
              initialMonth={initialMonth}
              initialYear={initialYear}
              onMonthChange={(m, y) => { setCurrentMonth(m); setCurrentYear(y); }}
              highlightedDate={selectedDate}
              highlightedWeekStart={selectedWeekStart}
            />
          </div>

          {/* Weight Progress gauge */}
          <div className="analytics-tile">
            <div className="cal-panel analytics-tile-panel">
              <WeightGaugeV2
                data={displayWeightProgress}
                loading={weightProgressLoading}
                error={weightProgressError}
                dayWeight={dailyWeightComparison?.dayWeight}
                dayWeightLastWeek={dailyWeightComparison?.dayWeightLastWeek}
              />
            </div>
          </div>

          {/* Macro Donut */}
          <div className="analytics-tile">
            <div className="cal-panel analytics-tile-panel">
              <h3 className="cal-panel-title">Macro Breakdown</h3>
              {totalMacroCal > 0 ? (
                <div className="macro-donut-container">
                  <div className="donut-wrapper">
                    <Doughnut data={donutData} options={donutOptions} />
                    <div className="donut-center-label">
                      <span className="donut-cal-value">{Math.round(displayMacros.calories)}</span>
                      <span className="donut-cal-unit">cal</span>
                    </div>
                  </div>
                  <div className="macro-legend">
                    <div className="macro-legend-item">
                      <span className="macro-dot" style={{ background: '#3b82f6' }} />
                      <span className="macro-label">Protein</span>
                      <span className="macro-value">{Math.round(displayMacros.protein_g)}g ({proteinPct}%)</span>
                    </div>
                    <div className="macro-legend-item">
                      <span className="macro-dot" style={{ background: '#10b981' }} />
                      <span className="macro-label">Carbs</span>
                      <span className="macro-value">{Math.round(displayMacros.carbs_g)}g ({carbsPct}%)</span>
                    </div>
                    <div className="macro-legend-item">
                      <span className="macro-dot" style={{ background: '#f59e0b' }} />
                      <span className="macro-label">Fat</span>
                      <span className="macro-value">{Math.round(displayMacros.fats_g)}g ({fatPct}%)</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-data-message">No nutrition data</div>
              )}
            </div>
          </div>

          {/* CIC Calculator */}
          <div className="analytics-tile">
            <div className="cal-panel analytics-tile-panel">
              <CICCalculatorTile
                clientId={clientId}
                avgCalories={cicData.avgCalories}
                periodStartWeight={cicData.periodStartWeight}
                periodEndWeight={cicData.periodEndWeight}
                periodDays={cicData.periodDays}
                periodLabel={cicData.periodLabel}
              />
            </div>
          </div>
        </div>

        {/* Bottom Half: Analytics Table */}
        <div className="analytics-bottom">
          <div className="analytics-table-container" ref={tableContainerRef}>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th className="col-date">Date</th>
                  <th className="col-num col-thick">Weight</th>
                  <th className="col-num col-thin">&Delta;</th>
                  <th className="col-num col-thick">Calories</th>
                  <th className="col-num col-thin">Carbs <span className="col-unit">(g)</span></th>
                  <th className="col-num col-thin">Protein <span className="col-unit">(g)</span></th>
                  <th className="col-num col-thin">Fat <span className="col-unit">(g)</span></th>
                  <th className="col-num col-thin">Sodium <span className="col-unit">(mg)</span></th>
                  <th className="col-num col-thick">Steps</th>
                  <th className="col-num col-thin">Sleep <span className="col-unit">(hr)</span></th>
                  <th className="col-num col-thin">Water <span className="col-unit">(oz)</span></th>
                  <th className="col-num col-thick">Stress</th>
                  <th className="col-num col-thin">Energy</th>
                </tr>
              </thead>
              <tbody ref={tbodyRef}>
                {tableRows.map(row => (
                  <tr
                    key={row.date}
                    className={`${!row.hasData ? 'no-data-row' : ''} ${isRowHighlighted(row.date) ? 'highlighted-row' : ''}`}
                    onClick={(e) => { if (row.hasData) handleTableRowClick(row.date, e.ctrlKey || e.metaKey); }}
                    style={{ cursor: row.hasData ? 'pointer' : 'default' }}
                  >
                    <td className="col-date">
                      <span
                        className={`date-link ${row.hasData ? 'clickable' : ''}`}
                        onClick={(e) => { e.stopPropagation(); if (row.hasData) setNutritionDetailDate(row.date); }}
                      >
                        {formatTableDate(row.date)}
                      </span>
                    </td>
                    <td className="col-num col-thick">{row.weight != null ? row.weight.toFixed(1) : '—'}</td>
                    <td className={`col-num col-thin ${row.prevDayChange != null ? (row.prevDayChange < 0 ? 'change-loss' : row.prevDayChange > 0 ? 'change-gain' : '') : ''}`}>
                      {row.prevDayChange != null ? `${row.prevDayChange > 0 ? '+' : ''}${row.prevDayChange.toFixed(1)}` : '—'}
                    </td>
                    <td className="col-num col-thick" {...cellStyle(row, 'calories', row.calories)}>{row.calories != null && row.calories > 0 ? Math.round(row.calories).toLocaleString() : '—'}</td>
                    <td className="col-num col-thin" {...cellStyle(row, 'carbs', row.carbs)}>{row.carbs != null && row.carbs > 0 ? Math.round(row.carbs) : '—'}</td>
                    <td className="col-num col-thin" {...cellStyle(row, 'protein', row.protein)}>{row.protein != null && row.protein > 0 ? Math.round(row.protein) : '—'}</td>
                    <td className="col-num col-thin" {...cellStyle(row, 'fat', row.fat)}>{row.fat != null && row.fat > 0 ? Math.round(row.fat) : '—'}</td>
                    <td className="col-num col-thin" {...cellStyle(row, 'sodium', row.sodium)}>{row.sodium != null && row.sodium > 0 ? Math.round(row.sodium).toLocaleString() : '—'}</td>
                    <td className="col-num col-thick" {...cellStyle(row, 'steps', row.steps)}>{row.steps != null && row.steps > 0 ? Math.round(row.steps).toLocaleString() : '—'}</td>
                    <td className="col-num col-thin" {...cellStyle(row, 'sleep', row.sleep)}>{row.sleep != null && row.sleep > 0 ? row.sleep.toFixed(1) : '—'}</td>
                    <td className="col-num col-thin" {...cellStyle(row, 'water', row.water)}>{row.water != null && row.water > 0 ? Math.round(row.water) : '—'}</td>
                    <td className="col-num col-thick" {...cellStyle(row, 'stress', row.stress)}>{row.stress != null ? Math.round(row.stress) : '—'}</td>
                    <td className="col-num col-thin" {...cellStyle(row, 'energy', row.energy)}>{row.energy != null ? Math.round(row.energy) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="footer-row">
                  <td className="col-date footer-label">{footerData.label}</td>
                  <td className="col-num col-thick">{footerData.weight != null ? footerData.weight.toFixed(1) : ''}</td>
                  <td className={`col-num col-thin ${footerData.weightDelta != null ? (footerData.weightDelta < 0 ? 'change-loss' : footerData.weightDelta > 0 ? 'change-gain' : '') : ''}`}>
                    {footerData.weightDelta != null ? `${footerData.weightDelta > 0 ? '+' : ''}${footerData.weightDelta.toFixed(1)}` : ''}
                  </td>
                  <td className="col-num col-thick" style={footerData.colors.calories ? { color: footerData.colors.calories } : undefined}>{footerData.calories != null ? Math.round(footerData.calories).toLocaleString() : '—'}</td>
                  <td className="col-num col-thin" style={footerData.colors.carbs ? { color: footerData.colors.carbs } : undefined}>{footerData.carbs != null ? Math.round(footerData.carbs) : '—'}</td>
                  <td className="col-num col-thin" style={footerData.colors.protein ? { color: footerData.colors.protein } : undefined}>{footerData.protein != null ? Math.round(footerData.protein) : '—'}</td>
                  <td className="col-num col-thin" style={footerData.colors.fat ? { color: footerData.colors.fat } : undefined}>{footerData.fat != null ? Math.round(footerData.fat) : '—'}</td>
                  <td className="col-num col-thin" style={footerData.colors.sodium ? { color: footerData.colors.sodium } : undefined}>{footerData.sodium != null ? Math.round(footerData.sodium).toLocaleString() : '—'}</td>
                  <td className="col-num col-thick" style={footerData.colors.steps ? { color: footerData.colors.steps } : undefined}>{footerData.steps != null ? Math.round(footerData.steps).toLocaleString() : '—'}</td>
                  <td className="col-num col-thin" style={footerData.colors.sleep ? { color: footerData.colors.sleep } : undefined}>{footerData.sleep != null ? footerData.sleep.toFixed(1) : '—'}</td>
                  <td className="col-num col-thin" style={footerData.colors.water ? { color: footerData.colors.water } : undefined}>{footerData.water != null ? Math.round(footerData.water) : '—'}</td>
                  <td className="col-num col-thick" style={footerData.colors.stress ? { color: footerData.colors.stress } : undefined}>{footerData.stress != null ? footerData.stress.toFixed(1) : '—'}</td>
                  <td className="col-num col-thin" style={footerData.colors.energy ? { color: footerData.colors.energy } : undefined}>{footerData.energy != null ? footerData.energy.toFixed(1) : '—'}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        </>
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .calendar-expanded-view {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 500px;
    background: transparent;
    animation: calExpandIn 0.2s ease-out;
  }

  @keyframes calExpandIn {
    from { opacity: 0; transform: scale(0.98); }
    to { opacity: 1; transform: scale(1); }
  }

  /* Header */
  .cal-expanded-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 24px;
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border-secondary);
    border-radius: var(--radius-large) var(--radius-large) 0 0;
  }

  .cal-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .cal-header-left svg {
    color: var(--accent-green);
  }

  .cal-header-left h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .cal-client-badge {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    padding: 4px 10px;
    background: var(--bg-tertiary);
    border-radius: 12px;
  }

  .cal-header-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .cal-minimize-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: transparent;
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s;
  }

  .cal-minimize-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }

  /* Top Half: 4-column layout */
  .analytics-top {
    display: grid;
    grid-template-columns: auto 1fr 1fr 1fr;
    gap: 16px;
    padding: 16px 24px;
    min-height: 0;
    align-items: stretch;
  }

  .analytics-calendar {
    max-height: 360px;
    overflow: hidden;
  }

  .analytics-tile {
    display: flex;
  }

  .analytics-tile-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .analytics-placeholder {
    opacity: 0.3;
    border-style: dashed;
  }

  /* Bottom Half: Analytics Table */
  .analytics-bottom {
    flex: 1;
    padding: 0 24px 16px;
    min-height: 200px;
    overflow: hidden;
  }

  .analytics-table-container {
    height: 100%;
    overflow: hidden;
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    background: var(--bg-secondary);
    display: flex;
    flex-direction: column;
  }

  .analytics-table {
    width: 100%;
    font-size: 13px;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .analytics-table thead {
    flex-shrink: 0;
    display: block;
    width: 100%;
    overflow-y: auto;
    scrollbar-gutter: stable;
    box-shadow: 0 3px 0 0 var(--text-tertiary, #555);
    z-index: 10;
    background: var(--bg-tertiary);
  }

  .analytics-table thead tr {
    display: table;
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  .analytics-table thead th {
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 10px 12px;
    border-bottom: none;
    white-space: nowrap;
  }

  .analytics-table tbody {
    flex: 1;
    display: block;
    overflow-y: auto;
    scrollbar-gutter: stable;
    min-height: 0;
  }

  .analytics-table tbody tr {
    display: table;
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  .analytics-table tbody td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--border-secondary);
    color: var(--text-primary);
  }

  .analytics-table .col-date {
    text-align: left;
    font-weight: 500;
  }

  .analytics-table .col-num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .analytics-table .date-link {
    color: var(--text-secondary);
    text-decoration: none;
  }

  .analytics-table .date-link.clickable {
    color: var(--accent-blue);
    cursor: pointer;
  }

  .analytics-table .date-link.clickable:hover {
    text-decoration: underline;
  }

  .analytics-table .no-data-row td {
    opacity: 0.4;
  }

  .analytics-table .highlighted-row td {
    background: var(--row-highlight);
  }

  .analytics-table .change-loss,
  .analytics-table .footer-row td.change-loss {
    color: var(--accent-green);
  }

  .analytics-table .change-gain,
  .analytics-table .footer-row td.change-gain {
    color: #ef4444;
  }

  .analytics-table tfoot {
    flex-shrink: 0;
    display: block;
    width: 100%;
    overflow-y: auto;
    scrollbar-gutter: stable;
    box-shadow: 0 -3px 0 0 var(--text-tertiary, #555);
    z-index: 10;
    background: var(--bg-tertiary);
  }

  .analytics-table tfoot tr {
    display: table;
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  .analytics-table .footer-row td {
    background: var(--bg-tertiary);
    font-weight: 600;
    padding: 10px 12px;
    border-top: none;
    border-bottom: none;
    color: var(--text-primary);
  }

  .analytics-table .footer-label {
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.5px;
  }

  /* Panels */
  .cal-panel {
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    padding: 16px;
  }

  .cal-panel-title {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: center;
  }

  /* Macro Donut */
  .macro-donut-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .donut-wrapper {
    position: relative;
    width: 140px;
    height: 140px;
  }

  .donut-center-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    display: flex;
    flex-direction: column;
    line-height: 1.1;
  }

  .donut-cal-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-primary);
  }

  .donut-cal-unit {
    font-size: 11px;
    color: var(--text-secondary);
    text-transform: uppercase;
  }

  .macro-legend {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .macro-legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }

  .macro-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .macro-label {
    color: var(--text-secondary);
    flex-shrink: 0;
  }

  .macro-value {
    color: var(--text-primary);
    font-weight: 500;
    margin-left: auto;
  }

  .no-data-message {
    text-align: center;
    color: var(--text-tertiary);
    font-size: 13px;
    padding: 24px 0;
  }

  /* Column dividers — thick for major groups, thin for individual columns */
  .analytics-table .col-thick {
    border-left: 2px solid var(--text-tertiary, #555);
  }

  .analytics-table .col-thin {
    border-left: 1px solid var(--border-secondary);
  }

  .analytics-table .col-unit {
    font-weight: 400;
    opacity: 0.7;
    font-size: 10px;
  }

  /* Responsive */
  @media (max-width: 1100px) {
    .analytics-top {
      grid-template-columns: auto 1fr 1fr;
    }

    .analytics-placeholder {
      display: none;
    }
  }

  @media (max-width: 768px) {
    .analytics-top {
      grid-template-columns: 1fr;
    }
  }

  .analytics-day-detail {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    animation: calExpandIn 0.15s ease-out;
  }

  .analytics-day-detail-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 24px 16px;
  }


`;

export default CalendarExpandedView;
